import React, { useEffect, useState } from 'react';
import { useForm, Controller } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import * as axiosInstance from '../utils/axiosinstace';
import AuthLayout from '../auth/AuthLayout';
import AuthField from '../auth/AuthField';
import { useAuthMotion } from '../auth/authMotion';
import styles from '../auth/AuthLayout.module.css';

const REDIRECT_SECONDS = 5;

// Same rule the reset flow enforces (auth:forgot.passwordHint). Choosing a
// password for the first time and choosing a replacement are the same act, so
// they now hold to the same standard — this screen previously accepted
// anything at all.
const isStrongPassword = (value) =>
    /^.{8,20}$/.test(value) &&
    /[!@#$%^&*()_+{}[\]:;<>,.?~\\-]/.test(value) &&
    /[A-Z]/.test(value);

// Everything on this page that differs between the two kinds of password link.
// The route is shared deliberately: the token is opaque, so only the server knows
// which kind it is — a second route could only repeat, or contradict, what the
// token says, and reconciling that always resolves to "trust the token". That
// makes a /reset prefix decoration that can lie.
//
// The error maps exist because the API answers in English only, and a dead link
// is the worst possible moment to switch languages on someone. Known codes are
// rendered from the locale bundle; anything unrecognised falls back to the
// server's own text.
const MODES = {
    set: {
        title: 'auth:setPassword.title',
        subtitle: 'auth:login.subtitle',
        forUser: 'auth:setPassword.forUser',
        success: 'auth:setPassword.success',
        errors: {
            INVALID_TOKEN: 'auth:setPassword.invalidLink',
            EXPIRED_TOKEN: 'auth:setPassword.expiredLink',
            USED_TOKEN: 'auth:setPassword.usedLink',
            SUPERSEDED_TOKEN: 'auth:setPassword.supersededLink',
        },
    },
    reset: {
        title: 'auth:resetPassword.title',
        subtitle: 'auth:resetPassword.subtitle',
        forUser: 'auth:resetPassword.forUser',
        success: 'auth:resetPassword.success',
        errors: {
            INVALID_TOKEN: 'auth:resetPassword.invalidLink',
            EXPIRED_TOKEN: 'auth:resetPassword.expiredLink',
            USED_TOKEN: 'auth:resetPassword.usedLink',
            SUPERSEDED_TOKEN: 'auth:resetPassword.supersededLink',
        },
    },
};

const SetPassword = (props) => {
    const { t } = useTranslation(['auth', 'common']);
    const history = useHistory();
    const v = useAuthMotion();
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm({ mode: 'onChange' });

    // An opaque, single-use invite token. It used to be base64 JSON that the
    // browser decoded and sent back as {email, username} — which meant anyone
    // could mint a link for any account. There is nothing left to decode here:
    // the server resolves the token and decides whether it is usable, so
    // validity now arrives as an API response rather than a decode failure.
    const token = props.match.params.user;

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [redirectIn, setRedirectIn] = useState(null);
    // Which kind of link this is, and whose account it belongs to. Both come from
    // the server — the token is opaque, so the browser cannot work either out.
    // 'set' is the DEFAULT rather than a loading state, so an API that has not
    // shipped `mode` yet behaves exactly as this page did before.
    const [mode, setMode] = useState('set');
    const [accountName, setAccountName] = useState('');
    // Set only once the server has told us the link is dead. There is no point
    // letting someone type a password twice into a link that cannot save.
    const [linkDead, setLinkDead] = useState(false);
    const password = watch('password');

    const copy = MODES[mode] || MODES.set;

    // Ask about the token before anyone types. ADVISORY: if the call itself fails
    // — offline, DNS, a 500 — we say nothing and leave the form usable, because
    // submitting will produce a real answer either way. Only a verdict FROM the
    // server locks the form.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axiosInstance.validateCredentialToken(token);
                if (!alive || !res) return;
                if (res.mode === 'reset') setMode('reset');
                if (res.status) {
                    if (res.userName) setAccountName(res.userName);
                    return;
                }
                // MODES[res.mode] rather than `copy`: copy is stale from the
                // render that scheduled this effect.
                const key = (MODES[res.mode] || MODES.set).errors[res.code];
                setError(key ? t(key) : res.message);
                setLinkDead(true);
            } catch (e) {
                // Deliberately silent — see above.
            }
        })();
        return () => { alive = false; };
    }, [token, t]);

    // Counts down, then leaves. The old version mutated a ref, so the number
    // never re-rendered, and its interval was never cleared — it could push a
    // route after the component had gone.
    useEffect(() => {
        if (redirectIn === null) return undefined;
        if (redirectIn <= 0) {
            history.push('/login');
            return undefined;
        }
        const id = setTimeout(() => setRedirectIn((n) => n - 1), 1000);
        return () => clearTimeout(id);
    }, [redirectIn, history]);

    const onSubmit = async (data) => {
        setError('');
        try {
            // Explicit body rather than {...data}: react-hook-form's payload is
            // whatever the form happens to hold, and only these three fields
            // belong on the wire.
            const response = await axiosInstance.addCredentials({
                token,
                password: data.password,
                confirmPassword: data.confirmPassword,
            });
            if (response.status) {
                // Localised rather than the server's English. Without this the
                // banner is mixed-language: "Successfully added credentials"
                // sitting directly above a Malay countdown.
                setSuccess(t(copy.success));
                setRedirectIn(REDIRECT_SECONDS);
            } else {
                const key = copy.errors[response.code];
                setError(key ? t(key) : response.message);
            }
        } catch (err) {
            // Previously console.log'd and nothing reached the screen.
            setError((err && err.message) || t('auth:login.genericError'));
        }
    };

    const done = redirectIn !== null;
    // Locked once the save has landed, and also when the server has already told
    // us the link is dead -- typing a password twice into a link that cannot save
    // it is exactly the experience the validate call exists to prevent.
    const locked = done || linkDead;

    return (
        <AuthLayout title={t(copy.title)} subtitle={t(copy.subtitle)}>
            <motion.form onSubmit={handleSubmit(onSubmit)} noValidate>
                <AnimatePresence initial={false}>
                    {(error || success) && (
                        <motion.div
                            key='setpw-banner'
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div
                                className={`${styles.banner} ${error ? '' : styles.bannerOk}`}
                                role='alert'
                            >
                                {error || success}
                                {success && redirectIn !== null && (
                                    <div style={{ fontWeight: 400, marginTop: 4 }}>
                                        {t('auth:setPassword.redirecting', { seconds: redirectIn })}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {accountName ? (
                    // The account this link belongs to. This is the point of the
                    // reset flow, not decoration: the farmer who needs one has
                    // usually forgotten their user name as well as their password,
                    // and this email is the only place they will be told it.
                    <p className={styles.hint} style={{ marginTop: 0, marginBottom: 16 }}>
                        {t(copy.forUser, { userName: accountName })}
                    </p>
                ) : null}

                <motion.div variants={v.formContainer} initial='hidden' animate='visible'>
                    <Controller
                        name='password'
                        control={control}
                        rules={{
                            required: t('auth:validation.enterAPassword'),
                            validate: (value) =>
                                isStrongPassword(value) || t('auth:validation.passwordStrength'),
                        }}
                        render={({ field }) => (
                            <AuthField
                                id='setpw-password'
                                label={t('common:fields.password')}
                                icon='fas fa-lock'
                                type='password'
                                canReveal
                                revealLabel={t('auth:login.showPassword')}
                                hideRevealLabel={t('auth:login.hidePassword')}
                                autoComplete='new-password'
                                disabled={locked}
                                placeholder={t('auth:login.passwordPlaceholder')}
                                hint={t('auth:forgot.passwordHint')}
                                error={errors.password && errors.password.message}
                                {...field}
                            />
                        )}
                    />

                    <Controller
                        name='confirmPassword'
                        control={control}
                        rules={{
                            required: t('auth:validation.confirmYourPassword'),
                            // Checked on the field rather than inside onSubmit, so the
                            // message lands under the input that's wrong.
                            validate: (value) =>
                                value === password || t('auth:validation.passwordsDoNotMatch'),
                        }}
                        render={({ field }) => (
                            <AuthField
                                id='setpw-confirm'
                                label={t('auth:forgot.confirmPassword')}
                                icon='fas fa-lock'
                                type='password'
                                canReveal
                                revealLabel={t('auth:login.showPassword')}
                                hideRevealLabel={t('auth:login.hidePassword')}
                                autoComplete='new-password'
                                disabled={locked}
                                placeholder={t('auth:forgot.confirmPassword')}
                                error={errors.confirmPassword && errors.confirmPassword.message}
                                {...field}
                            />
                        )}
                    />

                    <motion.div variants={v.item}>
                        <motion.button
                            type='submit'
                            className={styles.submit}
                            disabled={isSubmitting || locked}
                            aria-busy={isSubmitting}
                            whileHover={isSubmitting || locked ? undefined : { scale: 1.02 }}
                            whileTap={isSubmitting || locked ? undefined : { scale: 0.98 }}
                        >
                            {isSubmitting && !v.reduced && (
                                <span className={styles.spinner} aria-hidden='true' />
                            )}
                            {isSubmitting ? t('common:actions.saving') : t(copy.title)}
                        </motion.button>
                    </motion.div>

                    <motion.div className={styles.backRow} variants={v.item}>
                        <Link to='/login' className={styles.link}>
                            {t('auth:forgot.backToLogin')}
                        </Link>
                    </motion.div>
                </motion.div>
            </motion.form>
        </AuthLayout>
    );
};

export default SetPassword;
